import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

const RAG_API_URL = 'WebApi.asmx/api/ragChat/sendMessage'; // WebApi proxy to Semantic Service
const CHAT_SETTING_BASE = "WebApi.asmx/api/chatSettingService";

function debounce(fn, delay) {
    let t = null;
    let lastArgs = null;
    const d = (...a) => {
        lastArgs = a;
        if (t) clearTimeout(t);
        t = setTimeout(() => { t = null; fn(...lastArgs); }, delay);
    };
    d.cancel = () => { if (t) { clearTimeout(t); t = null; } lastArgs = null; };
    d.flush  = () => { if (t && lastArgs) { clearTimeout(t); t = null; fn(...lastArgs); } };
    return d;
}

// Helper to create a message node
const createNode = (role, text, parentId = null) => ({
    id: uuidv4(),
    parentId,
    children: [],
    selectedChildIndex: 0, // Which child path is currently active
    role, // 'user' or 'ai'
    text,
    timestamp: Date.now(),
    isTyping: false,
    sources: [] // Source topic objects [{topicId, title}] referenced in the response
});

const state = {
    // Active-chat tree (unchanged)
    nodes: {}, // Map of ID -> Node
    headId: null, // The current tip of the conversation
    rootIds: [], // Array of root node IDs (nodes with no parent)
    selectedRootIndex: 0, // Which root path is currently active
    isOpen: false,
    isGenerating: false,
    error: null,

    // Multi-chat metadata layer (Phase 14)
    chatList: [],            // [{ id, key, name, changedOn, version, value }]
    activeChatId: null,      // int UssId; null = empty state (D-03)
    hydrated: false,         // has GetChats completed at least once
    isLoadingChat: false,    // spinner while GetChats/LoadChat in flight
    saveIndicator: 'idle'    // 'idle' | 'saving' | 'ok' | 'error' (UI-03 hook)
};

const getters = {
    // Reconstruct the linear conversation from root to head
    currentThread: (state) => {
        if (!state.headId || !state.nodes[state.headId]) return [];

        const thread = [];
        let current = state.nodes[state.headId];

        while (current) {
            thread.unshift(current);
            if (!current.parentId) break;
            current = state.nodes[current.parentId];
        }
        return thread;
    },
    // Get sibling count and current index for a specific node to show arrows
    getNodeNavigation: (state) => (id) => {
        const node = state.nodes[id];
        if (!node) return null;

        // Handle root nodes (no parentId) - they are siblings of each other
        if (!node.parentId) {
            if (state.rootIds.length <= 1) return null;
            return {
                current: state.rootIds.indexOf(id) + 1,
                total: state.rootIds.length,
                hasMultiple: state.rootIds.length > 1
            };
        }

        const parent = state.nodes[node.parentId];
        if (!parent) return null;

        return {
            current: parent.children.indexOf(id) + 1,
            total: parent.children.length,
            hasMultiple: parent.children.length > 1
        };
    },
    // Get conversation history formatted for API
    getConversationHistory: (state, getters) => {
        const thread = getters.currentThread;
        return thread.map(node => ({
            role: node.role === 'ai' ? 'assistant' : 'user',
            content: node.text
        }));
    }
};

const mutations = {
    SET_OPEN(state, isOpen) {
        state.isOpen = isOpen;
    },
    SET_GENERATING(state, isGenerating) {
        state.isGenerating = isGenerating;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    ADD_NODE(state, node) {
        state.nodes[node.id] = node;

        if (!node.parentId) {
            // This is a root node - add to rootIds array
            state.rootIds = [...state.rootIds, node.id];
            state.selectedRootIndex = state.rootIds.length - 1;
        } else if (state.nodes[node.parentId]) {
            const parent = state.nodes[node.parentId];
            // Use spread to create a new array reference for better Vue reactivity
            parent.children = [...parent.children, node.id];
            // Automatically switch parent to point to this new child
            parent.selectedChildIndex = parent.children.length - 1;
        }

        // Update head to be this new node
        state.headId = node.id;
    },
    UPDATE_NODE_TEXT(state, { nodeId, text, sources }) {
        if (state.nodes[nodeId]) {
            state.nodes[nodeId].text = text;
            if (sources) {
                state.nodes[nodeId].sources = sources;
            }
        }
    },
    UPDATE_HEAD(state, nodeId) {
        state.headId = nodeId;
    },
    LOAD_STATE(state, loadedState) {
        Object.assign(state, loadedState);
        state.isGenerating = false; // Ensure we don't start in a stuck loading state
        state.error = null;
    },
    RESET_CHAT(state) {
        state.nodes = {};
        state.headId = null;
        state.rootIds = [];
        state.selectedRootIndex = 0;
        state.error = null;
    },

    // Multi-chat metadata mutations (Phase 14)
    SET_CHAT_LIST(state, chats) {
        state.chatList = chats || [];
    },
    SET_ACTIVE_CHAT_ID(state, id) {
        state.activeChatId = id;
    },
    SET_HYDRATED(state, flag) {
        state.hydrated = !!flag;
    },
    SET_LOADING_CHAT(state, flag) {
        state.isLoadingChat = !!flag;
    },
    SET_SAVE_INDICATOR(state, s) {
        state.saveIndicator = s;
    },
    UPDATE_CHAT_VERSION(state, { id, version }) {
        state.chatList = state.chatList.map(c =>
            c.id === id ? { ...c, version, changedOn: new Date().toISOString() } : c
        );
    },
    PUSH_CHAT(state, chat) {
        state.chatList = [...state.chatList, chat];
    },
    REMOVE_CHAT(state, id) {
        state.chatList = state.chatList.filter(c => c.id !== id);
    },
    UPDATE_CHAT_NAME(state, { id, name }) {
        state.chatList = state.chatList.map(c =>
            c.id === id ? { ...c, name } : c
        );
    },
    UPDATE_CHAT_VALUE(state, { id, value }) {
        state.chatList = state.chatList.map(c =>
            c.id === id ? { ...c, value } : c
        );
    }
};

// Module-scope save driver + debounced wrapper (Phase 14)
async function doSaveState(store, { isRetry = false } = {}) {
    const { state, commit } = store;
    const chat = state.chatList.find(c => c.id === state.activeChatId);
    if (!chat) return;
    commit('SET_SAVE_INDICATOR', 'saving');
    const valueJson = JSON.stringify({
        displayName: chat.name,
        nodes: state.nodes,
        headId: state.headId,
        rootIds: state.rootIds,
        selectedRootIndex: state.selectedRootIndex
    });
    const body = new FormData();
    body.append('id', chat.id);
    body.append('version', chat.version);
    body.append('value', valueJson);
    try {
        const res = await axios.post(CHAT_SETTING_BASE + '/SaveChat', body);
        if (res.data.success === true) {
            commit('UPDATE_CHAT_VERSION', { id: chat.id, version: res.data.version });
            commit('UPDATE_CHAT_VALUE', { id: chat.id, value: valueJson });
            commit('SET_SAVE_INDICATOR', 'ok');
            return;
        }
        if (res.data.errorCode === 'VERSION_CONFLICT') {
            // Another tab/session modified this chat. Do NOT auto-retry with the local
            // tree — that silently clobbers their work. Do NOT bump local chat.version
            // either: any subsequent save would then succeed against the new version
            // and overwrite the remote work the same way the auto-retry used to.
            // Stop here, surface the conflict; user must reload to merge.
            console.warn('SaveChat conflict — chat was modified elsewhere; reload required to merge.');
            commit('SET_SAVE_INDICATOR', 'error');
            return;
        }
        console.error('SaveChat failed:', res.data.errorMessage);
        commit('SET_SAVE_INDICATOR', 'error');
    } catch (e) {
        console.error('SaveChat network error:', e);
        if (!isRetry) return doSaveState(store, { isRetry: true });
        commit('SET_SAVE_INDICATOR', 'error');
    }
}
const debouncedSave = debounce(doSaveState, 500);

// Flush any pending save when the user navigates away or backgrounds the tab.
// Registered lazily on first hydrate (i.e. when an authenticated user actually
// uses the chat) so anonymous-user pageloads don't pay the bfcache penalty
// that beforeunload listeners impose.
let _flushListenersRegistered = false;
function ensureFlushListeners() {
    if (_flushListenersRegistered || typeof window === 'undefined') return;
    _flushListenersRegistered = true;
    const flushPending = () => {
        try { debouncedSave.flush(); } catch (e) { /* swallow — best effort */ }
    };
    window.addEventListener('pagehide', flushPending);
    window.addEventListener('beforeunload', flushPending);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushPending();
    });
}

const actions = {
    async toggleChat({ state, commit, dispatch }) {
        if (!state.isOpen && !state.hydrated) {
            await dispatch('hydrate');
        }
        commit('SET_OPEN', !state.isOpen);
    },

    // User sends a message - now calls the RAG API
    async sendMessage({ commit, dispatch, state, getters }, text) {
        const parentId = state.headId;
        const userNode = createNode('user', text, parentId);
        commit('ADD_NODE', userNode);
        commit('SET_ERROR', null);
        commit('SET_GENERATING', true);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000);

        try {
            const history = getters.currentThread
                .slice(0, -1)
                .map(node => ({
                    role: node.role === 'ai' ? 'assistant' : 'user',
                    content: node.text
                }));

            const response = await fetch(RAG_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                signal: controller.signal,
                body: JSON.stringify({
                    message: text,
                    history: history
                })
            });
            const data = await response.json();
            if (!response.ok || data.success === false) {
                const errorMessage = data.errorMessage || data.error || `HTTP error! status: ${response.status}`;
                throw new Error(errorMessage);
            }

            const aiNode = createNode('ai', data.answer || 'No response received.', userNode.id);
            aiNode.sources = data.sources || [];
            commit('ADD_NODE', aiNode);

            dispatch('saveState');
        } catch (error) {
            if (error.name === 'AbortError') {
                error.message = 'Request timed out. The AI service may be busy — please try again.';
            }
            console.error('Error sending message:', error);
            commit('SET_ERROR', error.message || 'Failed to get response from AI');

            const errorNode = createNode('ai', `Sorry, I encountered an error: ${error.message}. Please try again.`, userNode.id);
            commit('ADD_NODE', errorNode);
        } finally {
            clearTimeout(timeoutId);
            commit('SET_GENERATING', false);
            dispatch('saveState');
        }
    },

    // Go back to a previous node and branch off
    navigateToNode({ commit, state }, nodeId) {
        // Find the leaf of the branch starting at nodeId
        let currentId = nodeId;
        while (state.nodes[currentId] && state.nodes[currentId].children.length > 0) {
            const node = state.nodes[currentId];
            const childId = node.children[node.selectedChildIndex || 0];
            currentId = childId;
        }
        commit('UPDATE_HEAD', currentId);
    },

    // Set the current head directly to a specific message ID (or null)
    jumpToMessage({ commit }, nodeId) {
        commit('UPDATE_HEAD', nodeId);
    },

    // Switch between sibling versions
    switchVersion({ commit, state }, { nodeId, direction }) {
        if (state.isGenerating) { console.warn('switchVersion blocked: generation in progress'); return; }
        const node = state.nodes[nodeId];
        if (!node) return;

        let siblings, currentIndex, newIndex, siblingId;

        // Handle root nodes (no parentId)
        if (!node.parentId) {
            siblings = state.rootIds;
            currentIndex = siblings.indexOf(nodeId);
            newIndex = currentIndex + direction;

            // Wrap around
            if (newIndex < 0) newIndex = siblings.length - 1;
            if (newIndex >= siblings.length) newIndex = 0;

            siblingId = siblings[newIndex];
            state.selectedRootIndex = newIndex;
        } else {
            const parent = state.nodes[node.parentId];
            siblings = parent.children;
            currentIndex = siblings.indexOf(nodeId);
            newIndex = currentIndex + direction;

            // Wrap around
            if (newIndex < 0) newIndex = siblings.length - 1;
            if (newIndex >= siblings.length) newIndex = 0;

            siblingId = siblings[newIndex];
            parent.selectedChildIndex = newIndex;
        }

        // We need to find the new head (deepest child) of this sibling path
        let currentId = siblingId;
        while (state.nodes[currentId] && state.nodes[currentId].children.length > 0) {
            const n = state.nodes[currentId];
            currentId = n.children[n.selectedChildIndex || 0];
        }

        commit('UPDATE_HEAD', currentId);
    },

    // Reroll the last AI message - now calls the RAG API
    async rerollResponse({ commit, dispatch, state }, parentNodeId) {
        if (state.isGenerating) { console.warn('rerollResponse blocked: generation in progress'); return; }
        const parent = state.nodes[parentNodeId];
        if (!parent || parent.role !== 'user') return;

        commit('SET_GENERATING', true);
        commit('SET_ERROR', null);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000);

        try {
            const history = [];
            let currentId = parentNodeId;
            const nodesToInclude = [];

            while (currentId) {
                const node = state.nodes[currentId];
                if (node) {
                    nodesToInclude.unshift(node);
                    currentId = node.parentId;
                } else {
                    break;
                }
            }

            for (let i = 0; i < nodesToInclude.length - 1; i++) {
                const node = nodesToInclude[i];
                history.push({
                    role: node.role === 'ai' ? 'assistant' : 'user',
                    content: node.text
                });
            }

            const response = await fetch(RAG_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                signal: controller.signal,
                body: JSON.stringify({
                    message: parent.text,
                    history: history
                })
            });

            const data = await response.json();

            if (!response.ok || data.success === false) {
                const errorMessage = data.errorMessage || data.error || `HTTP error! status: ${response.status}`;
                throw new Error(errorMessage);
            }

            const newAiNode = createNode('ai', data.answer || 'No response received.', parentNodeId);
            newAiNode.sources = data.sources || [];
            commit('ADD_NODE', newAiNode);
        } catch (error) {
            if (error.name === 'AbortError') {
                error.message = 'Request timed out. The AI service may be busy — please try again.';
            }
            console.error('Error rerolling response:', error);
            commit('SET_ERROR', error.message || 'Failed to get new response');
        } finally {
            clearTimeout(timeoutId);
            commit('SET_GENERATING', false);
            dispatch('saveState');
        }
    },

    saveState(store) {
        debouncedSave(store);
    },

    async hydrate({ commit, dispatch }) {
        commit('SET_LOADING_CHAT', true);
        // Authenticated user is engaging with chat — wire up the unload-flush handlers
        // so the last AI reply isn't lost in the 500ms debounce window. Lazy registration
        // keeps anonymous pageloads free of the bfcache cost.
        ensureFlushListeners();
        try {
            const res = await axios.get(CHAT_SETTING_BASE + '/GetChats');
            if (res.data.success === true) {
                commit('SET_CHAT_LIST', res.data.chats || []);
                if (res.data.chats && res.data.chats.length > 0) {
                    const mostRecent = [...res.data.chats]
                        .sort((a, b) => new Date(b.changedOn) - new Date(a.changedOn))[0];
                    await dispatch('loadChat', mostRecent.id);
                }
                commit('SET_HYDRATED', true);
            } else {
                console.error('GetChats failed:', res.data.errorMessage);
            }
        } catch (e) {
            console.error('GetChats network error:', e);
        } finally {
            commit('SET_LOADING_CHAT', false);
        }
    },

    async loadChat({ commit, state }, id) {
        const chat = state.chatList.find(c => c.id === id);
        if (!chat) { console.error('loadChat: id not in chatList', id); return; }
        // GetChats (extended by Plan 14-00) returns `value = s.UssValue` per chat.
        // `chat.value` may be an empty string or a stub `{"displayName":"New Chat"}` for freshly-created
        // chats that have not yet been saved via SaveChat — treat missing tree fields as empty defaults.
        let parsed = {};
        try {
            parsed = chat.value ? JSON.parse(chat.value) : {};
        } catch (e) {
            console.error('loadChat: invalid UssValue JSON for chat', id, e);
            parsed = {};
        }
        commit('SET_ACTIVE_CHAT_ID', id);
        commit('LOAD_STATE', {
            nodes: parsed.nodes || {},
            headId: parsed.headId || null,
            rootIds: parsed.rootIds || [],
            selectedRootIndex: parsed.selectedRootIndex || 0
        });
    },

    async switchChat({ state, dispatch }, id) {
        if (state.isGenerating) { console.warn('switchChat blocked: generation in progress'); return; }
        if (state.activeChatId === id) return;
        debouncedSave.flush(); // force pending save of outgoing chat
        await dispatch('loadChat', id);
    },

    async createNewChat({ state, commit }) {
        if (state.isGenerating) { console.warn('createNewChat blocked: generation in progress'); return null; }
        try {
            const res = await axios.post(CHAT_SETTING_BASE + '/CreateChat', new FormData());
            if (res.data.success === true) {
                const newChat = {
                    id: res.data.id,
                    key: res.data.key,
                    name: res.data.name,
                    changedOn: new Date().toISOString(),
                    version: res.data.version,
                    value: null
                };
                commit('PUSH_CHAT', newChat);
                commit('SET_ACTIVE_CHAT_ID', res.data.id);
                commit('LOAD_STATE', { nodes: {}, headId: null, rootIds: [], selectedRootIndex: 0 });
                return res.data.id;
            }
            console.error('CreateChat failed:', res.data.errorMessage);
            return null;
        } catch (e) {
            console.error('CreateChat network error:', e);
            return null;
        }
    },

    async sendFirstMessage({ dispatch }, text) {
        const newId = await dispatch('createNewChat');
        if (!newId) return;
        await dispatch('sendMessage', text);
        const trimmed = (text || '').trim().slice(0, 50);
        if (trimmed) dispatch('renameChat', { id: newId, name: trimmed });
    },

    createChatPlaceholder({ commit, state }) {
        if (state.isGenerating) {
            console.warn('createChatPlaceholder blocked: generation in progress');
            return;
        }
        commit('SET_ACTIVE_CHAT_ID', null);
        commit('RESET_CHAT');
    },

    async deleteChat({ commit, state, dispatch }, id) {
        if (state.isGenerating) {
            console.warn('deleteChat blocked: generation in progress');
            return;
        }
        const body = new FormData();
        body.append('id', id);
        try {
            const res = await axios.post(CHAT_SETTING_BASE + '/DeleteChat', body);
            if (res.data.success === true) {
                const wasActive = state.activeChatId === id;
                commit('REMOVE_CHAT', id);
                if (wasActive) {
                    const remaining = [...state.chatList]
                        .sort((a, b) => new Date(b.changedOn) - new Date(a.changedOn));
                    if (remaining.length > 0) {
                        await dispatch('loadChat', remaining[0].id);
                    } else {
                        commit('SET_ACTIVE_CHAT_ID', null);
                        commit('RESET_CHAT');
                    }
                }
            } else {
                console.error('DeleteChat failed:', res.data.errorMessage);
            }
        } catch (e) {
            console.error('DeleteChat network error:', e);
        }
    },

    async renameChat({ commit, state }, { id, name }) {
        if (state.isGenerating) {
            console.warn('renameChat blocked: generation in progress');
            return;
        }
        const body = new FormData();
        body.append('id', id);
        body.append('name', name);
        try {
            const res = await axios.post(CHAT_SETTING_BASE + '/RenameChat', body);
            if (res.data.success === true) {
                commit('UPDATE_CHAT_NAME', { id, name });
                if (res.data.version) {
                    commit('UPDATE_CHAT_VERSION', { id, version: res.data.version });
                }
            } else {
                console.error('RenameChat failed:', res.data.errorMessage);
            }
        } catch (e) {
            console.error('RenameChat network error:', e);
        }
    },

    clearChat({ commit }) {
        debouncedSave.cancel();
        commit('RESET_CHAT');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
